import { useEffect, useMemo, useState } from 'react';
import { useTravelMode } from '@/modules/travel/useTravelMode';

export type GeolocationStatus = 'idle' | 'requesting' | 'granted' | 'denied' | 'unavailable';

export interface GeoCoords {
  lat: number;
  lng: number;
  accuracy: number;
}

export interface UseGeolocationResult {
  coords: GeoCoords | null;
  status: GeolocationStatus;
  error: string | null;
  request: () => void;
  /** Set when the user has an active travel override that's shadowing GPS. */
  travelCity: string | null;
}

const STORAGE_KEY = 'hottr.map.last_coords';

const readCached = (): GeoCoords | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as GeoCoords;
    if (
      typeof parsed.lat === 'number' &&
      typeof parsed.lng === 'number' &&
      typeof parsed.accuracy === 'number'
    ) {
      return parsed;
    }
  } catch {
    // ignore parse / storage errors
  }
  return null;
};

const writeCached = (coords: GeoCoords) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(coords));
  } catch {
    // ignore storage errors
  }
};

/**
 * Browser geolocation with sane defaults and a localStorage cache so the
 * map can render immediately on revisit while a fresher fix is requested.
 *
 * Cached coords (if any) are returned synchronously; a new fix replaces
 * them when it arrives.
 */
export const useGeolocation = (autoRequest = true): UseGeolocationResult => {
  const [coords, setCoords] = useState<GeoCoords | null>(() => readCached());
  const [status, setStatus] = useState<GeolocationStatus>('idle');
  const [error, setError] = useState<string | null>(null);

  const request = () => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setStatus('unavailable');
      setError('Geolocation is not supported in this browser.');
      return;
    }

    setStatus('requesting');
    setError(null);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const next: GeoCoords = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy,
        };
        setCoords(next);
        writeCached(next);
        setStatus('granted');
      },
      (err) => {
        if (err.code === err.PERMISSION_DENIED) {
          setStatus('denied');
          setError('Location permission denied.');
        } else {
          setStatus('unavailable');
          setError(err.message || 'Could not determine location.');
        }
      },
      {
        enableHighAccuracy: false,
        maximumAge: 60_000,
        timeout: 12_000,
      },
    );
  };

  useEffect(() => {
    if (autoRequest) request();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRequest]);

  // Travel mode override: when present, return the override coords with
  // a sentinel accuracy and a non-null status so the map renders.
  const { travel } = useTravelMode();
  const effective = useMemo<GeoCoords | null>(() => {
    if (travel) {
      return { lat: travel.city.lat, lng: travel.city.lng, accuracy: 100 };
    }
    return coords;
  }, [travel, coords]);

  return {
    coords: effective,
    status: travel ? 'granted' : status,
    error,
    request,
    travelCity: travel?.city.label ?? null,
  };
};
